<template>
    <button
    class="button-primary"
    :disabled="disabled"
    :class="[buttonStyle, buttonSize, {'with-external-label': withExternalLabel}]"
    @click.stop="emit('clicked')">
        <!-- Button Icon -->
        <app-icon v-if="icon && !withExternalLabel" class="icon" :class="{ 'icon-right': iconPosition === 'right' }"/>
        <!-- Button Icon -->
        <div v-if="icon && withExternalLabel" class="icon-with-external-label-wrapper">
            <app-icon class="icon" :class="{ 'icon-right': iconPosition === 'right' }"/>
        </div>
        <!-- Button label -->
        <div v-if="label && (buttonStyle !== 'round' || buttonStyle === 'round' && withExternalLabel)" class="label">{{ label }}</div>
    </button>
</template>

<script setup>
/* Vue */
import { computed, defineAsyncComponent } from 'vue'
import { iconModules } from '@/assets/icons/iconModules'
/* Emmitters */
const emit = defineEmits(['clicked'])

/** Props received:
@label {String}
@icon {String}
@iconPosition {String} can be [left (default), right]
@buttonStyle {String} can be [square (default), round]
@buttonSize {String} can be [large (default), medium, small]
@withExternalLabel {Boolean} to add label below button
@disabled {Boolean}
*/
const props = defineProps({
    label: String,
	icon: String,
    iconPosition: {
        type: String,
        default: 'left'
    },
	buttonStyle: {
        type: String,
        default: 'square'
    },
    buttonSize: {
        type: String,
        default: 'large'
    },
    withExternalLabel: Boolean,
    disabled: Boolean
})

/* Computed */
const appIcon = computed(() => {
    const icon = props.icon
	return defineAsyncComponent(iconModules[`/src/assets/icons/${icon}.svg`])
})
</script>

<style lang="scss" scoped>
.button-primary {
    width: max-content;

    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    @include H7-Medium;
    color: var(--secondary-dark-1);
    cursor: pointer;

    svg.icon {
        flex-shrink: 0;

        width: 16px;
        height: 16px;
        pointer-events: none;

        :deep(.change-color-fill) {
            fill: var(--secondary-light-4);
        }

        &.icon-right {
            order: 2;
        }
    }

    &:not(.with-external-label) {
        padding: 0 16px;
        gap: 8px;

        color: var(--secondary-light-4);
        background-color: var(--primary);
        box-shadow: var(--box-shadow-3);

        /* Style classes */
        &.square {
            border-radius: 8px;
        }

        &.round {
            border-radius: 50%;

            &.large {
                width: 56px;
            }

            &.medium {
                width: 48px;
            }

            &.small {
                width: 40px;
            }
        }
        /* Size classes */
        &.large {
            height: 56px;
        }

        &.medium {
            height: 48px;
        }

        &.small {
            height: 40px;
        }

        &:hover {
            background-color: var(--button-primary-state-hover);
        }

        &:active {
            background-color: var(--button-primary-state-press);
        }
    }

    &.with-external-label {
        flex-direction: column;
        gap: 4px;

        .icon-with-external-label-wrapper {
            width: 40px;
            height: 40px;

            display: flex;
            align-items: center;
            justify-content: center;

            background-color: var(--primary);
            box-shadow: var(--box-shadow-3);
            border-radius: 50%;
        }

        &:hover {
            .icon-with-external-label-wrapper {
                background-color: var(--button-primary-state-hover);
            }
        }

        &:active {
            .icon-with-external-label-wrapper {
                background-color: var(--button-primary-state-press);
            }
        }
    }

    &:disabled {
        cursor: default;
        pointer-events: none;
        opacity: 0.24;
    }
}
</style>
